import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useAdminData } from "../useAdminData";
import { ALL_GAMES, PROVIDERS, PROVIDER_NAMES, platformLabel, EVENT_TYPE_LABELS } from "../catalog";
import { Panel, Skeleton, EmptyState, ErrorState, PageHeader, KpiCard, MetricStrip, Button, fmtNum, fmtDate } from "../Primitives";
import { Search, X } from "lucide-react";

export default function Games() {
  const [q, setQ] = useState("");
  const [provider, setProvider] = useState("");
  const [sort, setSort] = useState("clicks");
  const [selected, setSelected] = useState(null);
  const { data: counts, loading, error, reload } = useAdminData(async (api) => {
    const { data } = await api.get("/admin/rankings");
    return Object.fromEntries((data.games || []).map((g) => [g.key, g.count]));
  }, []);

  const games = useMemo(() => {
    let list = ALL_GAMES.map((g) => ({ ...g, events: counts?.[g.id] || 0 }));
    if (provider) list = list.filter((g) => g.provider === provider);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((g) => g.name.toLowerCase().includes(s) || g.id.includes(s));
    return [...list].sort(sort === "name" ? (a, b) => a.name.localeCompare(b.name) : (a, b) => b.events - a.events);
  }, [counts, provider, q, sort]);

  return (
    <div data-testid="games-page">
      <PageHeader title="Jogos" meta={`${ALL_GAMES.length} jogos no catálogo. Selecione um jogo para ver o detalhamento por plataforma.`} />
      <div className="flex flex-wrap items-center gap-2 pb-4 mb-5 border-b border-[var(--border)]">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted2)]" aria-hidden="true" />
          <input data-testid="games-search" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Pesquisar jogo"
            placeholder="Nome ou ID do jogo" className="mia-input !pl-9 !h-8 !py-0" />
        </div>
        <select data-testid="games-provider" aria-label="Provedor" value={provider} onChange={(e) => setProvider(e.target.value)}>
          <option value="">Provedor: todos</option>
          {PROVIDERS.filter((p) => p.gamesCount > 0).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="games-sort" aria-label="Ordenação" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="clicks">Mais eventos</option>
          <option value="name">Nome (A–Z)</option>
        </select>
        <span className="ml-auto mono text-[12px] text-[var(--muted2)]">{games.length} resultados</span>
      </div>
      {error && <ErrorState message={error} onRetry={reload} />}

      {loading && !counts ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">{Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-[180px]" />)}</div>
      ) : games.length === 0 ? (
        <Panel><EmptyState title="Nenhum jogo encontrado" description="Ajuste a busca ou o filtro de provedor." /></Panel>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="games-grid">
          {games.map((g) => (
            <button key={`${g.provider}-${g.id}`} onClick={() => setSelected(g)} data-testid={`game-card-${g.id}`}
              className="mia-panel mia-hover-card overflow-hidden text-left">
              <div className="aspect-square bg-[var(--surface2)]">
                {g.image && <img src={g.image} alt="" loading="lazy" className="w-full h-full object-cover" />}
              </div>
              <div className="px-2.5 py-2 border-t border-[var(--border)]">
                <p className="text-[13px] truncate">{g.name}</p>
                <div className="flex items-center justify-between mt-0.5 text-[11px]">
                  <span className="text-[var(--muted2)] truncate">{PROVIDER_NAMES[g.provider] || g.providerName}</span>
                  <span className="mono text-[var(--muted)]">{fmtNum(g.events)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      <AnimatePresence>{selected && <GameDetail key={selected.id} game={selected} onClose={() => setSelected(null)} />}</AnimatePresence>
    </div>
  );
}

function GameDetail({ game, onClose }) {
  const reduce = useReducedMotion();
  const { data, loading, error, reload } = useAdminData(async (api) => (await api.get("/admin/game-detail", { params: { game_id: game.id } })).data, [game.id]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const t = data?.totals || {};

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="game-detail-modal">
      <motion.div className="absolute inset-0 bg-black/60" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.14 }} />
      <motion.aside role="dialog" aria-modal="true" aria-label={game.name}
        className="relative w-full max-w-xl h-full overflow-y-auto mia-scroll bg-[var(--surface)] border-l border-[var(--border)]"
        initial={{ x: reduce ? 0 : 32, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: reduce ? 0 : 32, opacity: 0 }} transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}>
        <header className="flex items-center gap-3 p-4 border-b border-[var(--border)] sticky top-0 bg-[var(--surface)] z-10">
          {game.image && <img src={game.image} alt="" className="w-11 h-11 rounded-[4px] object-cover" />}
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] font-semibold truncate">{game.name}</h2>
            <p className="text-[12px] text-[var(--muted)]">{PROVIDER_NAMES[game.provider] || game.providerName} · ID {game.id}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="modal-close" aria-label="Fechar"><X size={16} /></Button>
        </header>
        <div className="p-4 space-y-4">
          {error && <ErrorState message={error} onRetry={reload} />}
          {loading && !data ? <Skeleton className="h-40" /> : data && (
            <>
              <MetricStrip cols={4}>
                <KpiCard label="Aberturas" value={t.game_open} testid="game-opens" />
                <KpiCard label="Saídas" value={t.platform_exit_click} testid="game-exits" />
                <KpiCard label="Sinais" value={t.signal_view} testid="game-signals" />
                <KpiCard label="Sessões" value={data.sessions} testid="game-sessions" />
              </MetricStrip>
              <Panel title="Por plataforma">
                {data.platforms.length === 0 ? <EmptyState title="Sem eventos por plataforma" /> : (
                  <table className="ops-table">
                    <thead><tr><th>Plataforma</th><th className="!text-right">Sessões</th><th className="!text-right">Aberturas</th><th className="!text-right">Saídas</th></tr></thead>
                    <tbody>{data.platforms.map((p) => (
                      <tr key={p.platform} data-testid={`platform-row-${p.platform}`}>
                        <td>{platformLabel(p.platform)}</td><td className="num">{fmtNum(p.sessions)}</td>
                        <td className="num">{fmtNum(p.game_opens)}</td><td className="num">{fmtNum(p.exit_clicks)}</td>
                      </tr>))}
                    </tbody>
                  </table>
                )}
              </Panel>
              <Panel title="Eventos recentes" description="Sessão exibida como identificador anônimo truncado.">
                {data.recent.length === 0 ? <EmptyState title="Nenhum evento recente" /> : (
                  <table className="ops-table">
                    <tbody>{data.recent.slice(0, 30).map((r, i) => (
                      <tr key={i}>
                        <td className="mono text-[12px] text-[var(--muted)] whitespace-nowrap">{fmtDate(r.server_ts)}</td>
                        <td>{EVENT_TYPE_LABELS[r.type] || r.type}</td>
                        <td className="text-[var(--muted)]">{r.platform ? platformLabel(r.platform) : "—"}</td>
                        <td className="mono text-[12px] text-[var(--muted2)]">{r.session || "—"}</td>
                      </tr>))}
                    </tbody>
                  </table>
                )}
              </Panel>
            </>
          )}
        </div>
      </motion.aside>
    </div>
  );
}
